import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config/api";

const C = {
  bg: "#08090d",
  surface: "#12141b",
  panel: "#0b1020",
  border: "#272b36",
  text: "#f6f7fb",
  muted: "#a7adbb",
  quiet: "#707789",
  blue: "#3b82f6",
  blue2: "#60a5fa",
  green: "#4ade80",
  red: "#fb7185",
};

const ROLE_ORDER = ["TOP", "JUNGLE", "MIDDLE", "BOTTOM", "UTILITY"];
const ROLE_LABEL = { TOP: "TOP", JUNGLE: "JGL", MIDDLE: "MID", BOTTOM: "BOT", UTILITY: "SUP" };

function fmtBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value.toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
}

function sortedPlayers(team) {
  return [...(team?.players || [])].sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));
}

function championIcon(champion, version) {
  return `https://ddragon.leagueoflegends.com/cdn/${version}/img/champion/${champion}.png`;
}

function CompactScoutCard({ player, side, ddVersion }) {
  const isOne = side === "one";
  const color = isOne ? C.green : C.red;
  const rgb = isOne ? "74,222,128" : "251,113,133";
  const champions = (player.champions || []).slice(0, 5);
  const fallbackChampions = champions.length ? champions : (player.games || []).slice(0, 5).map((game) => ({
    champion: game.champion,
    games: 1,
    winrate: game.win ? 100 : 0,
  }));

  return (
    <article style={{
      height: 230,
      border: `1px solid rgba(${rgb},0.42)`,
      borderTop: `2px solid rgba(${rgb},0.9)`,
      borderRadius: 13,
      background: isOne ? "linear-gradient(180deg,#111a12,#09090b)" : "linear-gradient(180deg,#1a1111,#09090b)",
      padding: 12,
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      overflow: "hidden",
      boxShadow: "0 8px 24px rgba(0,0,0,0.42)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
        <div style={{ minWidth: 0 }}>
          <strong style={{ display: "block", color: C.text, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{player.gameName || "Unknown"}</strong>
          <span style={{ color: C.quiet, fontSize: 11 }}>{ROLE_LABEL[player.role] || player.role || "-"} · {player.rank || "Unranked"}</span>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <strong style={{ display: "block", color, fontSize: 14 }}>{Number(player.score || 0).toFixed(2)}</strong>
          <span style={{ color: C.quiet, fontSize: 10 }}>{player.gamesCount || 0}g</span>
        </div>
      </div>

      <div style={{ border: `1px solid rgba(${rgb},0.14)`, borderRadius: 10, background: "#0c0c0e", padding: 9, flex: 1, minHeight: 0, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "28px 1fr 42px 30px", gap: 6, color: C.quiet, fontSize: 9, fontWeight: 900, textTransform: "uppercase", marginBottom: 6 }}>
          <span />
          <span>Champion</span>
          <span style={{ textAlign: "center" }}>WR</span>
          <span style={{ textAlign: "right" }}>G</span>
        </div>
        <div style={{ display: "grid", gap: 5 }}>
          {fallbackChampions.map((champion, index) => (
            <div key={`${champion.champion}-${index}`} style={{ display: "grid", gridTemplateColumns: "28px 1fr 42px 30px", gap: 6, alignItems: "center", border: `1px solid ${C.border}`, borderRadius: 8, background: "#181b23", padding: "5px 6px", minWidth: 0 }}>
              <img
                src={championIcon(champion.champion, ddVersion)}
                alt={champion.champion}
                style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover" }}
                onError={(event) => { event.currentTarget.style.display = "none"; }}
              />
              <strong style={{ color: C.text, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{champion.champion}</strong>
              <span style={{ color: Number(champion.winrate) >= 55 ? C.green : Number(champion.winrate) < 45 ? C.red : C.muted, fontSize: 11, fontWeight: 800, textAlign: "center" }}>{Math.round(Number(champion.winrate || 0))}%</span>
              <span style={{ color: C.muted, fontSize: 10, textAlign: "right" }}>{champion.games || 1}g</span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function TeamGrid({ title, team, side, winnerId, ddVersion }) {
  const players = sortedPlayers(team);
  const isWinner = team?.teamId === winnerId;
  const color = side === "one" ? C.green : C.red;
  return (
    <section style={{ border: `1px solid ${isWinner ? "rgba(74,222,128,0.45)" : C.border}`, borderRadius: 16, background: C.surface, padding: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, color }}>{title}</h3>
        <span style={{ color: isWinner ? C.green : C.quiet, fontSize: 12, fontWeight: 800 }}>{isWinner ? "Winner" : "Reference team"}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 10 }}>
        {players.map((player, index) => (
          <CompactScoutCard key={`${player.gameName}-${index}`} player={player} side={side} ddVersion={ddVersion} />
        ))}
      </div>
    </section>
  );
}

function ImpactSummaryStrip({ summary }) {
  const diff = Number(summary?.diff || 0);
  const items = [
    { label: "Team One sum", value: Number(summary?.team1Score || 0), color: C.green },
    { label: "Difference", value: diff, color: diff >= 0 ? C.green : C.red },
    { label: "Team Two sum", value: Number(summary?.team2Score || 0), color: C.red },
  ];

  return (
    <section style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
      {items.map((item) => (
        <div key={item.label} style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface, padding: 16, textAlign: "center" }}>
          <span style={{ color: C.quiet, fontSize: 12, textTransform: "uppercase", fontWeight: 900 }}>{item.label}</span>
          <strong style={{ display: "block", color: item.color, fontSize: 30, marginTop: 8 }}>{item.value.toFixed(2)}</strong>
        </div>
      ))}
    </section>
  );
}

function fmtBytesLabel(file) {
  return `${file.path} - ${fmtBytes(file.size)}`;
}

export default function ExperimentalPage() {
  const location = useLocation();
  const labReturn = location.state?.labReturn || null;
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState(labReturn?.fileId || "");
  const [summary, setSummary] = useState(labReturn?.summary || null);
  const [selectedIndex, setSelectedIndex] = useState(labReturn?.selectedIndex || 0);
  const [caseData, setCaseData] = useState(labReturn?.caseData || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ddVersion, setDdVersion] = useState("14.21.1");
  const skipSummaryFetch = useRef(Boolean(labReturn?.summary));
  const skipCaseFetch = useRef(Boolean(labReturn?.caseData));

  useEffect(() => {
    fetch("https://ddragon.leagueoflegends.com/api/versions.json")
      .then((res) => res.json())
      .then((versions) => setDdVersion(versions[0] || "14.21.1"))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/experimental/files`)
      .then((res) => res.json())
      .then((data) => {
        const nextFiles = data.files || [];
        setFiles(nextFiles);
        if (!labReturn?.fileId && nextFiles[0]) setFileId(nextFiles[0].id);
      })
      .catch((err) => setError(err.message));
  }, [labReturn?.fileId]);

  useEffect(() => {
    if (!fileId) return;
    if (skipSummaryFetch.current) {
      skipSummaryFetch.current = false;
      return;
    }
    setLoading(true);
    setError("");
    fetch(`${API_BASE_URL}/experimental/summary?file=${encodeURIComponent(fileId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setSummary(data);
        setSelectedIndex(data.cases?.[0]?.index || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [fileId]);

  useEffect(() => {
    if (!fileId || !summary) return;
    if (skipCaseFetch.current) {
      skipCaseFetch.current = false;
      return;
    }
    fetch(`${API_BASE_URL}/experimental/case?file=${encodeURIComponent(fileId)}&index=${selectedIndex}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setCaseData(data);
      })
      .catch((err) => setError(err.message));
  }, [fileId, selectedIndex, summary]);

  const selectedSummary = useMemo(() => {
    return (summary?.cases || []).find((item) => item.index === selectedIndex);
  }, [summary, selectedIndex]);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text }}>
      <header style={{ borderBottom: `1px solid ${C.border}`, background: "rgba(8,9,13,0.9)" }}>
        <div style={{ width: "min(1480px, calc(100% - 32px))", margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ color: C.text, textDecoration: "none", fontWeight: 900 }}>ImpactGame</Link>
          <Link to="/scout" style={{ color: C.blue2, textDecoration: "none", fontWeight: 800 }}>Scout</Link>
        </div>
      </header>

      <main style={{ width: "min(1480px, calc(100% - 32px))", margin: "0 auto", padding: "34px 0 60px" }}>
        <h1 style={{ margin: 0, fontSize: 42 }}>Experimental Lab</h1>
        <p style={{ color: C.muted, marginTop: 10, maxWidth: 820 }}>
          Explore frozen SoloQ datasets, compare Team One vs Team Two, and load any reference game directly into the Scout view.
        </p>

        <section style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 180px 180px 180px", gap: 12, marginTop: 24 }}>
          <select value={fileId} onChange={(e) => setFileId(e.target.value)} style={{ background: C.surface, color: C.text, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12 }}>
            {files.map((file) => <option key={file.id} value={file.id}>{fmtBytesLabel(file)}</option>)}
          </select>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, padding: 12, color: C.muted }}>{loading ? "Loading..." : `${summary?.casesCollected || 0} cases`}</div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, padding: 12 }}>
            <span style={{ color: C.quiet, fontSize: 11 }}>Accuracy</span>
            <strong style={{ display: "block", color: C.text }}>{summary?.accuracy ?? 0}%</strong>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, padding: 12 }}>
            <span style={{ color: C.quiet, fontSize: 11 }}>Correct</span>
            <strong style={{ display: "block", color: C.green }}>{summary?.correct || 0}/{summary?.evaluatedCases || 0}</strong>
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, padding: 12 }}>
            <span style={{ color: C.quiet, fontSize: 11 }}>Requests</span>
            <strong style={{ display: "block", color: C.blue2 }}>{summary?.requestCount || 0}</strong>
          </div>
        </section>

        {error && <div style={{ marginTop: 14, color: C.red }}>{error}</div>}

        {summary && (
          <section style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, marginTop: 18, alignItems: "start" }}>
            <aside style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface, padding: 12, maxHeight: 760, overflow: "auto" }}>
              {(summary.cases || []).map((item) => (
                <button
                  key={item.index}
                  type="button"
                  onClick={() => setSelectedIndex(item.index)}
                  style={{ width: "100%", textAlign: "left", border: `1px solid ${selectedIndex === item.index ? C.blue : C.border}`, borderRadius: 12, background: selectedIndex === item.index ? "rgba(59,130,246,0.12)" : C.panel, color: C.text, padding: 12, marginBottom: 8, cursor: "pointer" }}
                >
                  <strong>#{item.index + 1} {item.matchId}</strong>
                  <div style={{ color: item.correct ? C.green : C.red, fontSize: 12, marginTop: 5 }}>
                    Team One vs Team Two · diff {item.diff.toFixed(2)} · {item.correct ? "correct" : "wrong"}
                  </div>
                </button>
              ))}
            </aside>

            <div>
              {selectedSummary && (
                  <section style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface, padding: 16, marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                      <div>
                        <h2 style={{ margin: 0 }}>Team One vs Team Two</h2>
                        <div style={{ color: C.muted, marginTop: 8 }}>
                          Prediction: team {selectedSummary.predictedTeamId} · Winner: team {selectedSummary.winnerTeamId} · Difference {selectedSummary.diff.toFixed(2)}
                        </div>
                      </div>
                      {caseData && (
                        <Link
                          to="/scout"
                          state={{
                            experimentalScout: {
                              team1: caseData.team1.players,
                              team2: caseData.team2.players,
                              sourceMatchId: selectedSummary.matchId,
                              labReturn: { fileId, selectedIndex, summary, caseData },
                            }
                          }}
                          style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: 40, borderRadius: 10, background: C.blue, color: "white", padding: "0 14px", textDecoration: "none", fontWeight: 900, whiteSpace: "nowrap" }}
                        >
                          Load in Scout
                        </Link>
                      )}
                    </div>
                  </section>
                )}

              {caseData && (
                  <>
                    <div style={{ display: "grid", gap: 14 }}>
                      <TeamGrid title="Team One" team={caseData.team1} side="one" winnerId={caseData.summary.winnerTeamId} ddVersion={ddVersion} />
                      <ImpactSummaryStrip summary={caseData.summary} />
                      <TeamGrid title="Team Two" team={caseData.team2} side="two" winnerId={caseData.summary.winnerTeamId} ddVersion={ddVersion} />
                    </div>

                  <section style={{ border: `1px solid ${C.border}`, borderRadius: 16, background: C.surface, padding: 16, marginTop: 16 }}>
                    <h3 style={{ margin: 0 }}>Reference game</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0,1fr))", gap: 10, marginTop: 14 }}>
                      {(caseData.participants || []).map((p, index) => (
                        <div key={`${p.gameName}-${index}`} style={{ border: `1px solid ${p.win ? "rgba(74,222,128,0.36)" : "rgba(251,113,133,0.34)"}`, borderRadius: 12, background: C.panel, padding: 10, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <img src={championIcon(p.champion, ddVersion)} alt={p.champion} style={{ width: 30, height: 30, borderRadius: 7 }} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                            <div style={{ minWidth: 0 }}>
                              <strong style={{ display: "block", color: p.win ? C.green : C.red, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.champion}</strong>
                              <span style={{ color: C.muted, fontSize: 12 }}>{ROLE_LABEL[p.role] || p.role} · {p.kills}/{p.deaths}/{p.assists}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                </>
              )}
            </div>
          </section>
        )}
      </main>

      <style>{`
        @media (max-width: 1200px) {
          main section {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
